// Whether the legacy "Import API access key" can authenticate (WALK F1).
//
// That key is the operator's Import_Access_Token UUID (GET /tour_operator/api_keys/). The import API
// authenticates API_Credential rows only (backend apiv2/views/import_views/utils.py,
// demo/lib/api_credential_auth.py): a UUID works only through a credential that carries it (legacy_key = the
// UUID, key_prefix = its first 8 characters), and only while that credential is active, unexpired and
// tour_operator-scoped. Migration 1420 made one for the tokens of its day; since 2026-10-28 (backend plan
// legacy-import-keys, D5) every production tenant's default key is its UUID, and the backend returns
// import_access_token null when the UUID does not authenticate. A sandbox's key is never its UUID: no key
// crosses environments (demo/lib/api_keys.py). This check stays as a second guard for a backend that still
// returns a dead UUID; drop it once that backend is gone.

/** `works` / `refused`: decided; `unknown`: the page cannot tell (shown, as before); `pending`: the
 *  credential list is still loading. */
export type LegacyKeyStatus = 'works' | 'refused' | 'unknown' | 'pending';

export interface CredentialRow {
    scope: string;
    status: string;
    key_prefix?: string | null;
    expires: string | null;
}

/** The tenant's credential list (GET /api_credential/): `rows` null when it could not be read (no
 *  `api_credential.list`, an error); `complete` false when the page holds only part of it. */
export interface CredentialList {
    loading: boolean;
    rows: CredentialRow[] | null;
    complete: boolean;
}

/** key_prefix of the credentials migration 1420 made: the first 8 characters of the UUID. */
const MIGRATED_PREFIX_LENGTH = 8;

export const legacyImportKeyStatus = (
    token: string,
    { environment, isFranchise, list, now = Date.now() }: { environment?: string | null; isFranchise?: boolean; list: CredentialList; now?: number },
): LegacyKeyStatus => {
    if (environment === 'sandbox') {
        return 'refused';
    }
    // The list of a franchise parent holds its licensees' keys, not the parent's own (GRID_FILTER_FRANCHISE_SCOPE).
    if (isFranchise) {
        return 'unknown';
    }
    if (list.loading) {
        return 'pending';
    }
    if (!list.rows) {
        return 'unknown';
    }
    const prefix = String(token).slice(0, MIGRATED_PREFIX_LENGTH);
    const backed = list.rows.some((row) => (
        row.scope === 'tour_operator'
        && row.status === 'active'
        && !(row.expires && Date.parse(row.expires) <= now)
        && row.key_prefix === prefix
    ));
    if (backed) {
        return 'works';
    }
    return list.complete ? 'refused' : 'unknown';
};
